import React from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import classNames from 'classnames';
import styled from 'styled-components';

import { getMarketPositionTable } from 'apis/RecommendationsAPI';
import LoaderRibbed from 'components/lib/LoaderRibbed';
import { formatPrice } from 'utils/helpers/numbers';

const Wrapper = styled.div`
  width: 100%;
  padding-top: 10px;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 30px 0;
`;

const EmptyMessage = styled.div`
  color: var(--white-60);
  font-size: 12px;
  text-align: center;
  padding: 20px 0;
`;

const Table = styled.div`
  display: table;
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
`;

const Row = styled.div`
  display: table-row;
`;

const HeaderCell = styled.div`
  display: table-cell;
  width: 70px;
  font-size: 10px;
  color: var(--white-50);
  padding-bottom: 8px;
  border-bottom: 1px solid var(--white-15);
  text-align: left;
`;

const FirstHeaderCell = styled(HeaderCell)`
  width: auto;
  padding-left: 20px;
`;

const LastHeaderCell = styled(HeaderCell)`
  padding-right: 20px;
`;

const Cell = styled.div`
  display: table-cell;
  width: 70px;
  font-size: 12px;
  color: var(--white-50);
  padding: 10px 0;
  border-bottom: 1px solid var(--white-10);
  background-color: transparent;
  text-align: left;

  &.primary {
    color: var(--white);
    background-color: var(--white-10);
  }

  &.last-row {
    border-bottom: none;
  }
`;

const NameCell = styled(Cell)`
  width: auto;
  padding-left: 20px;
`;

const NameCellContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const RankNumber = styled.span`
  flex-shrink: 0;
  width: 16px;
  font-size: 12px;
`;

const CommunityName = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;

  &.primary {
    font-family: var(--font-bold);
  }
`;

const CommunityNameText = styled.span`
  color: var(--white);
  font-size: 14px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
`;

const PrimaryTag = styled.span`
  flex-shrink: 0;
  font-family: var(--font-regular);
  font-size: 12px;
  color: var(--white-60);
`;

const ScoreCell = styled(Cell)`
  color: var(--white);
`;

const LastCell = styled(Cell)`
  padding-right: 20px;
`;

type Props = {
  communityId?: number;
};

const MarketPositionTable = ({ communityId }: Props) => {
  const navigate = useNavigate();

  const openCommunityAICI = (rowCommunityId: number | null) => {
    if (!rowCommunityId) {
      return;
    }
    navigate(`/ai-competitive-intelligence/overall-market-position?communityId=${rowCommunityId}`);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['market-position-table', communityId],
    queryFn: () => getMarketPositionTable(communityId ?? -1),
    enabled: Boolean(communityId),
    refetchOnWindowFocus: false,
  });

  const careTypes = data?.care_types ?? [];
  const rows = data?.rows ?? [];

  if (isLoading) {
    return (
      <Wrapper>
        <LoaderContainer data-test-id="market-position-table-loading">
          <LoaderRibbed active />
        </LoaderContainer>
      </Wrapper>
    );
  }

  if (rows.length === 0) {
    return (
      <Wrapper>
        <EmptyMessage data-test-id="market-position-table-empty">No comparison data available yet.</EmptyMessage>
      </Wrapper>
    );
  }

  return (
    <Wrapper data-test-id="market-position-table">
      <Table>
        <Row>
          <FirstHeaderCell>RANK</FirstHeaderCell>
          <HeaderCell>SCORE</HeaderCell>
          {careTypes.map((careType) => (
            <HeaderCell key={careType.abv}>{careType.abv} MIN</HeaderCell>
          ))}
          <LastHeaderCell>REVIEWS</LastHeaderCell>
        </Row>

        {rows.map((row, index) => {
          const isLastRow = index === rows.length - 1;

          return (
            <Row key={row.community_id ?? row.community_name}>
              <NameCell
                data-test-id={`market-position-table-row-${row.community_id ?? row.community_name}`}
                className={classNames({ primary: row.is_primary, 'last-row': isLastRow })}
              >
                <NameCellContent>
                  <RankNumber>{row.rank}</RankNumber>
                  <CommunityName className={classNames({ primary: row.is_primary })}>
                    <CommunityNameText onClick={() => openCommunityAICI(row.community_id)}>
                      {row.community_name}
                    </CommunityNameText>
                    {row.is_primary && <PrimaryTag>(My Community)</PrimaryTag>}
                  </CommunityName>
                </NameCellContent>
              </NameCell>
              <ScoreCell className={classNames({ primary: row.is_primary, 'last-row': isLastRow })}>
                {row.score}
              </ScoreCell>
              {careTypes.map((careType) => {
                const rate = row.min_rates[careType.abv];
                return (
                  <Cell key={careType.abv} className={classNames({ primary: row.is_primary, 'last-row': isLastRow })}>
                    {rate != null ? formatPrice(rate) : '-'}
                  </Cell>
                );
              })}
              <LastCell className={classNames({ primary: row.is_primary, 'last-row': isLastRow })}>
                {row.reviews_average != null ? row.reviews_average.toFixed(1) : '-'}
              </LastCell>
            </Row>
          );
        })}
      </Table>
    </Wrapper>
  );
};

export default MarketPositionTable;
